export default function useCaptcha() {
  const { captcha } = useRuntimeConfig().public;

  const {
    turnstileSiteKey: turnstileSiteKeyEnv,
    turnstileAlwaysShow: turnstileAlwaysShowEnv,
    recaptchaSiteKey: recaptchaSiteKeyEnv,
  } = captcha;

  const { authConfigsFromApi } = useFeatureFlagsNew();

  const captchaServices = computed<CaptchaServices[]>(() => {
    return authConfigsFromApi.value.captcha_services || ['turnstile'];
  });

  const turnstileSiteKey = computed<string>(() => {
    // return '2x00000000000000000000AB'; // always error
    // return '3x00000000000000000000FF'; // always interactive
    // return '1x00000000000000000000AA'; // always success

    if (authConfigsFromApi.value.turnstile_site_key) {
      return authConfigsFromApi.value.turnstile_site_key;
    }

    return turnstileSiteKeyEnv || '';
  });

  const recaptchaSiteKey = computed<string>(() => {
    if (authConfigsFromApi.value.recaptcha_site_key) {
      return authConfigsFromApi.value.recaptcha_site_key;
    }

    return recaptchaSiteKeyEnv || '';
  });

  const isTurnstileEnabled = computed<boolean>(() => {
    return (
      captchaServices.value.includes('turnstile') &&
      turnstileSiteKey.value !== ''
    );
  });

  const isRecaptchaEnabled = computed<boolean>(() => {
    return (
      captchaServices.value.includes('recaptcha') &&
      recaptchaSiteKey.value !== ''
    );
  });

  const isCaptchaEnabled = computed<boolean>(() => {
    const apiEnabledCaptcha = authConfigsFromApi.value.enable_captcha;

    if (
      apiEnabledCaptcha === false ||
      captchaServices.value.length === 0 ||
      (!isTurnstileEnabled.value && !isRecaptchaEnabled.value)
    ) {
      return false;
    }

    return apiEnabledCaptcha || false;
  });

  const captchaStyle = computed<AuthConfigs['captcha_style']>(() => {
    const style = authConfigsFromApi.value.captcha_style;

    if (style === 'dark' || style === 'light') {
      return style;
    }

    return 'dark';
  });

  const isInvisibleCaptcha = computed<boolean>(() => {
    return authConfigsFromApi.value.captcha_style === 'invisible';
  });

  const isCaptchaValidateCpfEnabled = computed<boolean>(() => {
    if (!isCaptchaEnabled.value) {
      return false;
    }

    return authConfigsFromApi.value.enable_captcha_validate_cpf === true;
  });

  const isCaptchaRegisterEnabled = computed<boolean>(() => {
    if (!isCaptchaEnabled.value) {
      return false;
    }

    return authConfigsFromApi.value.enable_captcha_register === true;
  });

  const isCaptchaLoginEnabled = computed<boolean>(() => {
    if (!isCaptchaEnabled.value) {
      return false;
    }

    return authConfigsFromApi.value.enable_captcha_login === true;
  });

  const isCaptchaRecoverPasswordEnabled = computed<boolean>(() => {
    if (!isCaptchaEnabled.value) {
      return false;
    }

    return authConfigsFromApi.value.enable_captcha_recover_password === true;
  });

  const isCaptchaResetPasswordEnabled = computed<boolean>(() => {
    if (!isCaptchaEnabled.value) {
      return false;
    }

    return authConfigsFromApi.value.enable_captcha_reset_password === true;
  });

  return {
    turnstileAlwaysShowEnv,
    isTurnstileEnabled,
    isRecaptchaEnabled,
    turnstileSiteKey,
    recaptchaSiteKey,
    isCaptchaEnabled,
    captchaStyle,
    isInvisibleCaptcha,
    isCaptchaValidateCpfEnabled,
    isCaptchaRegisterEnabled,
    isCaptchaLoginEnabled,
    isCaptchaRecoverPasswordEnabled,
    isCaptchaResetPasswordEnabled,
  };
}
